import React, { useState, useEffect } from 'react';

const LoginAdmin = ({ 
    isVisible, 
    isAdmin, 
    setIsAdmin, 
    onBack, 
    news = [], 
    onAddNews, 
    onDeleteNews, 
    isNewsSubmitting, 
    Icons,
    API_BASE 
}) => {
    // --- State برای بخش ورود ---
    const [adminUsername, setAdminUsername] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // --- State برای نظرات (مدیریت) ---
    const [allComments, setAllComments] = useState([]);
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'

    // --- State برای فرم خبر جدید ---
    const [newNewsTitle, setNewNewsTitle] = useState('');
    const [newNewsText, setNewNewsText] = useState('');

    // --- State برای لایسنس‌ها ---
    const [licenses, setLicenses] = useState([]);

    // --- State های جدید برای تب‌بندی اصلی و گزارشات همکاران ---
    const [activeMainTab, setActiveMainTab] = useState('dashboard'); // 'dashboard' | 'reseller_reports'
    const [resellerStats, setResellerStats] = useState([]);
    const [maxSales, setMaxSales] = useState(1);
    const [statsLoading, setStatsLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null); // وضعیت بارگذاری هنگام تغییر دسترسی همکار

    // --- فچ کردن همه نظرات (چه تایید شده چه نشده) ---
    const fetchAllComments = async () => {
        try {
            const approvedRes = await fetch(`${API_BASE}/comments.php?approved=1`);
            const approvedData = await approvedRes.json();
            const pendingRes = await fetch(`${API_BASE}/comments.php?approved=0`);
            const pendingData = await pendingRes.json();
            const combined = [
                ...(Array.isArray(approvedData) ? approvedData : []),
                ...(Array.isArray(pendingData) ? pendingData : [])
            ];
            combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setAllComments(combined);
        } catch (error) {
            console.error("Error fetching admin comments:", error);
        }
    };

    // --- فچ کردن لایسنس‌ها ---
    const fetchLicenses = async () => {
        try {
            const res = await fetch(`${API_BASE}/licenses.php?product=EtaNex`);
            const data = await res.json();
            if (data.success) {
                setLicenses(data.licenses);
            }
        } catch (err) {
            console.error('Error fetching licenses:', err);
        }
    };

    // --- فچ کردن آمار فروش همکاران از بک‌اند اختصاصی شما ---
    const fetchResellerStats = async () => {
        setStatsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin_reports.php?action=get_resellers_stats`);
            const data = await res.json();
            if (data.success && data.resellers) {
                setResellerStats(data.resellers);
                // پیدا کردن بیشترین فروش جهت تنظیم نسبت طول نمودارها
                const max = Math.max(...data.resellers.map(r => r.sales), 1);
                setMaxSales(max);
            }
        } catch (err) {
            console.error('Error fetching reseller stats:', err);
        } finally {
            setStatsLoading(false);
        }
    };

    // --- تابع ثبت تغییرات دسترسی و سهمیه همکار در دیتابیس بدون رفتن به phpMyAdmin ---
    const handleUpdateResellerAccess = async (resellerId, updatedProducts, updatedCredits) => {
        setUpdatingId(resellerId);
        try {
            const res = await fetch(`${API_BASE}/admin_reports.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_reseller_access',
                    reseller_id: resellerId,
                    allowed_products: updatedProducts,
                    allowed_credits: parseInt(updatedCredits) || 0
                })
            });
            const data = await res.json();
            if (data.success) {
                // بروزرسانی فرانت‌اند به صورت محلی
                setResellerStats(prev => prev.map(r => 
                    r.id === resellerId 
                        ? { ...r, allowed_products: updatedProducts, allowed_credits: parseInt(updatedCredits) || 0 } 
                        : r
                ));
            } else {
                alert(data.error || 'خطا در ثبت تغییرات دسترسی همکار');
            }
        } catch (err) {
            console.error('Error updating reseller:', err);
            alert('خطا در ارتباط با سرور جهت اعمال دسترسی');
        } finally {
            setUpdatingId(null);
        }
    };

    // تغییر وضعیت تیک محصول برای همکار
    const handleToggleProduct = (reseller, productKey) => {
        let currentProducts = reseller.allowed_products 
            ? reseller.allowed_products.split(',').map(p => p.trim()).filter(Boolean) 
            : [];
        
        if (currentProducts.includes(productKey)) {
            currentProducts = currentProducts.filter(p => p !== productKey);
        } else {
            currentProducts.push(productKey);
        }
        
        const updatedProductsString = currentProducts.join(',');
        handleUpdateResellerAccess(reseller.id, updatedProductsString, reseller.allowed_credits || 0);
    };

    // افکت برای بارگذاری اطلاعات پس از لاگین موفق ادمین
    useEffect(() => {
        if (isAdmin && isVisible) {
            fetchAllComments();
            fetchLicenses();
            fetchResellerStats();
        }
    }, [isAdmin, isVisible]);

    // --- تایید کردن نظر ---
    const handleApproveComment = async (id) => {
        try {
            const response = await fetch(`${API_BASE}/comments.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id })
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.error);
            setAllComments(prev => prev.map(c => c.id === id ? { ...c, is_approved: 1 } : c));
        } catch (error) {
            alert('خطا در تایید نظر: ' + error.message);
        }
    };

    // --- حذف کردن نظر ---
    const handleDeleteCommentInternal = async (id) => {
        if (!window.confirm('آیا از حذف این نظر مطمئن هستید؟')) return;
        try {
            const response = await fetch(`${API_BASE}/comments.php`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id })
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.error);
            setAllComments(allComments.filter(c => c.id !== id));
        } catch (error) {
            alert('خطا در حذف: ' + error.message);
        }
    };

    // --- هندلر ورود ادمین ---
    const handleLogin = (e) => {
        e.preventDefault();
        if (adminUsername === 'admin1997' && adminPassword === '@Kia13761376') {
            setIsAdmin(true);
            setAdminPassword('');
            setAdminUsername('');
        } else {
            alert('نام کاربری یا رمز عبور اشتباه است');
        }
    };

    // --- هندلر خروج ---
    const handleLogout = () => {
        setIsAdmin(false);
        if (onBack) onBack();
    };

    // --- هندلر ثبت خبر جدید ---
    const handleSubmitNews = async (e) => {
        e.preventDefault();
        if (!newNewsTitle.trim() || !newNewsText.trim()) return;
        if (onAddNews) {
            await onAddNews({ title: newNewsTitle, text: newNewsText });
            setNewNewsTitle('');
            setNewNewsText('');
        }
    };

    const pendingComments = allComments.filter(c => c.is_approved == 0);
    const approvedComments = allComments.filter(c => c.is_approved == 1);

    return (
        <div id="page-admin" className={`page-section ${isVisible ? '' : 'hidden-page'}`}>
            <style>{`
                .login-box-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 800px;
                    height: 65vh;
                    min-height: 500px;
                    background: #081b29;
                    box-shadow: 0 0 50px 0 #00a6bc;
                    border: 3px solid #00ffff;
                    overflow: hidden;
                    border-radius: 8px;
                }
                .neon-input {
                    border: none;
                    outline: none;
                    background: none;
                    border-bottom: 2px solid #eee;
                    width: 100%;
                    padding: 10px 10px 10px 0;
                    font-size: 17px;
                    color: #0ef;
                    transition: border-color 0.3s;
                }
                .neon-input::placeholder {
                    color: #eee;
                    opacity: 0.7;
                }
                .neon-input:focus {
                    border-bottom-color: #0ef;
                }
                .neon-submit {
                    border: none;
                    outline: none;
                    width: 100%;
                    margin-top: 25px;
                    padding: 12px 0;
                    font-size: 20px;
                    border-radius: 40px;
                    letter-spacing: 1px;
                    cursor: pointer;
                    background: linear-gradient(45deg, #0ef, #c800ff);
                    color: #fff;
                    font-weight: bold;
                    transition: transform 0.2s;
                }
                .neon-submit:hover {
                    transform: scale(1.02);
                    box-shadow: 0 0 15px #0ef;
                }
                .tab-btn {
                    padding: 8px 16px;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: bold;
                    transition: all 0.3s;
                }
                .tab-btn.active {
                    background: rgba(234, 179, 8, 0.2);
                    color: #fbbf24;
                    border: 1px solid rgba(234, 179, 8, 0.5);
                }
                .tab-btn.inactive {
                    color: #94a3b8;
                }
                .licenses-table th, .licenses-table td {
                    padding: 10px 12px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                }
                .licenses-table tbody tr:hover {
                    background: rgba(55, 65, 81, 0.3);
                }
                .admin-main-tab {
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-size: 15px;
                    font-weight: bold;
                    transition: all 0.3s;
                    border: 1px solid transparent;
                    cursor: pointer;
                }
                .admin-main-tab.active {
                    background: rgba(14, 239, 255, 0.1);
                    color: #0ef;
                    border-color: rgba(14, 239, 255, 0.4);
                    box-shadow: 0 0 15px rgba(14, 239, 255, 0.1);
                }
                .admin-main-tab.inactive {
                    color: #94a3b8;
                }
                .admin-main-tab.inactive:hover {
                    color: #fff;
                    background: rgba(255, 255, 255, 0.05);
                }
                
                /* استایل دکمه‌های تیک‌زدنی نئونی محصولات */
                .neon-toggle-btn {
                    cursor: pointer;
                    padding: 5px 12px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: bold;
                    transition: all 0.2s;
                    border: 1px solid #334155;
                    background: #0f172a;
                    color: #94a3b8;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .neon-toggle-btn.active {
                    background: rgba(14, 239, 255, 0.15);
                    color: #0ef;
                    border-color: #0ef;
                    box-shadow: 0 0 8px rgba(14, 239, 255, 0.3);
                }
                .neon-toggle-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
            `}</style>

            <div className="min-h-screen bg-[#081b29] flex flex-col">
                {/* هدر بالایی پنل ادمین */}
                <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center shadow-lg relative z-20">
                    <div className="flex items-center gap-3">
                        <div className="bg-yellow-500/20 p-2 rounded-lg text-yellow-500">
                            <Icons.Settings size={24} />
                        </div>
                        <h1 className="text-xl font-bold text-white">پنل مدیریت ارکانی عمران</h1>
                    </div>
                    <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                        بازگشت به سایت <Icons.LogOut size={16} />
                    </button>
                </div>

                <div className="flex-grow flex items-center justify-center p-4">
                    {!isAdmin ? (
                        /* فرم ورود نئونی با حفظ ۱۰۰٪ ساختار قبلی */
                        <div className="login-box-wrapper grid grid-cols-1 md:grid-cols-2">
                            <div className="flex flex-col justify-center items-center p-8 relative z-10">
                                <h2 className="text-3xl font-bold text-white mb-2">خوش آمدید</h2>
                                <p className="text-[#0ef] mb-10 text-sm">ورود به پنل مدیریت</p>
                                
                                <form onSubmit={handleLogin} className="w-full max-w-[260px] flex flex-col items-center">
                                    <div className="w-full mb-6">
                                        <input 
                                            type="text" 
                                            className="neon-input" 
                                            placeholder="نام کاربری" 
                                            value={adminUsername} 
                                            onChange={(e) => setAdminUsername(e.target.value)} 
                                        />
                                    </div>
                                    <div className="w-full mb-8 relative">
                                        <input 
                                            type={showPassword ? "text" : "password"} 
                                            className="neon-input" 
                                            placeholder="رمز عبور" 
                                            value={adminPassword} 
                                            onChange={(e) => setAdminPassword(e.target.value)} 
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setShowPassword(!showPassword)} 
                                            className="absolute left-0 top-3 text-[#0ef] hover:text-white transition-colors"
                                        >
                                            {showPassword ? <Icons.EyeOff size={18} /> : <Icons.Eye size={18} />}
                                        </button>
                                    </div>
                                    <button type="submit" className="neon-submit">ورود</button>
                                </form>
                            </div>

                            <div className="hidden md:flex flex-col justify-center items-end text-right p-10 relative bg-gradient-to-r from-[#0ef] to-[#c800ff]" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 20% 100%)" }}>
                                <div className="ml-auto w-full max-w-[280px] mt-[-50px]">
                                    <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-md">ارکانی عمران</h2>
                                    <p className="text-lg text-white/90 leading-relaxed font-medium drop-shadow-sm">
                                        توسعه دهنده تخصصی‌ترین ابزارهای مهندسی عمران و محاسبات سازه
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* بخش مدیریت پس از لاگین موفق ادمین */
                        <div className="w-full container mx-auto fade-in h-full">
                            
                            {/* نووار منوی اصلی برای سوییچ بین داشبورد و گزارش همکاران */}
                            <div className="flex gap-4 mb-6 border-b border-slate-800 pb-4 justify-center md:justify-start">
                                <button 
                                    onClick={() => setActiveMainTab('dashboard')} 
                                    className={`admin-main-tab ${activeMainTab === 'dashboard' ? 'active' : 'inactive'}`}
                                >
                                    داشبورد مدیریت محتوا
                                </button>
                                <button 
                                    onClick={() => setActiveMainTab('reseller_reports')} 
                                    className={`admin-main-tab ${activeMainTab === 'reseller_reports' ? 'active' : 'inactive'}`}
                                >
                                    گزارش و مدیریت دسترسی همکاران
                                </button>
                            </div>

                            {/* محتوای شرطی بر اساس تب فعال */}
                            {activeMainTab === 'dashboard' ? (
                                /* تب اول: داشبورد اصلی بدون تغییر */
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 fade-in">
                                    
                                    {/* بخش انتشار خبر */}
                                    <div className="lg:col-span-2">
                                        <div className="glass-panel p-6 rounded-2xl border border-yellow-500/20">
                                            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2 border-b border-slate-700 pb-4">
                                                <Icons.FileText className="text-yellow-500" />
                                                انتشار خبر جدید
                                            </h3>
                                            <form onSubmit={handleSubmitNews} className="grid md:grid-cols-2 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className="block text-slate-400 text-xs mb-2">عنوان خبر</label>
                                                    <input 
                                                        required 
                                                        type="text" 
                                                        value={newNewsTitle} 
                                                        onChange={(e) => setNewNewsTitle(e.target.value)} 
                                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-yellow-500 outline-none" 
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-slate-400 text-xs mb-2">متن خبر</label>
                                                    <textarea 
                                                        required 
                                                        rows="4" 
                                                        value={newNewsText} 
                                                        onChange={(e) => setNewNewsText(e.target.value)} 
                                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 outline-none transition-colors resize-none"
                                                    ></textarea>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <button 
                                                        disabled={isNewsSubmitting} 
                                                        type="submit" 
                                                        className="bg-yellow-500 text-slate-900 font-bold py-3 px-6 rounded-lg hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 w-full md:w-auto"
                                                    >
                                                        {isNewsSubmitting ? 'در حال ارسال...' : 'انتشار در سایت'}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>

                                    {/* بخش مدیریت اخبار */}
                                    <div>
                                        <div className="glass-panel p-6 rounded-2xl h-full">
                                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 border-b border-slate-700 pb-4">
                                                <Icons.FileText className="text-sky-400" />
                                                مدیریت اخبار ({news.length})
                                            </h3>
                                            <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                                                {news.map(item => (
                                                    <div key={item.id} className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 flex justify-between items-start group hover:border-slate-600 transition-colors">
                                                        <div>
                                                            <h4 className="font-bold text-white text-sm mb-1">{item.title}</h4>
                                                            <p className="text-xs text-slate-500 truncate w-48">{item.text}</p>
                                                            <span className="text-[10px] text-slate-600 mt-1 block">
                                                                {new Date(item.created_at).toLocaleDateString('fa-IR')}
                                                            </span>
                                                        </div>
                                                        <button 
                                                            onClick={() => onDeleteNews && onDeleteNews(item.id)} 
                                                            className="text-red-500 hover:bg-red-500/10 p-2 rounded transition-colors" 
                                                            title="حذف خبر"
                                                        >
                                                            <Icons.Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* بخش مدیریت نظرات پیشرفته */}
                                    <div>
                                        <div className="glass-panel p-6 rounded-2xl h-full flex flex-col">
                                            <div className="flex items-center justify-between border-b border-slate-700 pb-4 mb-4">
                                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                                    <Icons.MessageSquare className="text-emerald-400" />
                                                    مدیریت نظرات
                                                </h3>
                                                <div className="flex gap-2 bg-slate-900 rounded-lg p-1">
                                                    <button 
                                                        onClick={() => setActiveTab('pending')} 
                                                        className={`tab-btn ${activeTab === 'pending' ? 'active' : 'inactive'}`}
                                                    >
                                                        منتظر ({pendingComments.length})
                                                    </button>
                                                    <button 
                                                        onClick={() => setActiveTab('approved')} 
                                                        className={`tab-btn ${activeTab === 'approved' ? 'active' : 'inactive'}`}
                                                    >
                                                        تاییدشده ({approvedComments.length})
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2 flex-grow">
                                                {(activeTab === 'pending' ? pendingComments : approvedComments).length === 0 && (
                                                    <p className="text-center text-slate-500 text-sm mt-10">هیچ نظری در این بخش وجود ندارد.</p>
                                                )}
                                                {(activeTab === 'pending' ? pendingComments : approvedComments).map(comment => (
                                                    <div key={comment.id} className={`bg-slate-900/50 p-4 rounded-xl border flex flex-col gap-2 transition-colors ${comment.is_approved == 1 ? 'border-slate-800 hover:border-emerald-500/50' : 'border-yellow-500/30 hover:border-yellow-500'}`}>
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <h4 className="font-bold text-white text-sm">{comment.name}</h4>
                                                                    {comment.is_approved == 0 && (
                                                                        <span className="text-[10px] bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-full">در انتظار تایید</span>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-slate-300 mt-1 leading-relaxed">{comment.text}</p>
                                                                <span className="text-[10px] text-slate-600 mt-2 block">
                                                                    {new Date(comment.created_at).toLocaleString('fa-IR')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="flex gap-2 justify-end mt-2 pt-2 border-t border-slate-800/50">
                                                            {comment.is_approved == 0 && (
                                                                <button 
                                                                    onClick={() => handleApproveComment(comment.id)} 
                                                                    className="text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors"
                                                                >
                                                                    <Icons.CheckCircle size={14} /> تایید کردن
                                                                </button>
                                                            )}
                                                            <button 
                                                                onClick={() => handleDeleteCommentInternal(comment.id)} 
                                                                className="text-red-400 hover:bg-red-500/10 hover:text-red-300 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors"
                                                            >
                                                                <Icons.Trash2 size={14} /> حذف
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* جدول لایسنس‌های صادر شده EtaNex */}
                                    <div className="lg:col-span-2">
                                        <div className="glass-panel p-6 rounded-2xl">
                                            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2 border-b border-slate-700 pb-4">
                                                <Icons.CheckCircle className="text-emerald-400" />
                                                لایسنس‌های صادر شده EtaNex
                                                <span className="text-sm text-slate-400 font-normal mr-2">({licenses.length} مورد)</span>
                                            </h3>
                                            
                                            {licenses.length === 0 ? (
                                                <p className="text-slate-500 text-center py-8">هنوز هیچ لایسنس فعالی برای EtaNex ثبت نشده است.</p>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm text-right licenses-table">
                                                        <thead className="text-slate-400 border-b border-slate-700">
                                                            <tr>
                                                                <th className="py-2 px-3">#</th>
                                                                <th className="py-2 px-3">نام</th>
                                                                <th className="py-2 px-3">موبایل</th>
                                                                <th className="py-2 px-3">شناسه سخت‌افزار (HWID)</th>
                                                                <th className="py-2 px-3">کد لایسنس</th>
                                                                <th className="py-2 px-3">تاریخ فعال‌سازی</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="text-white">
                                                            {licenses.map((lic, idx) => (
                                                                <tr key={lic.id} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors">
                                                                    <td className="py-3 px-3 text-slate-500">{idx + 1}</td>
                                                                    <td className="py-3 px-3">{lic.name || '---'}</td>
                                                                    <td className="py-3 px-3" dir="ltr">{lic.mobile || '---'}</td>
                                                                    <td className="py-3 px-3 font-mono text-xs text-slate-300">{lic.hwid}</td>
                                                                    <td className="py-3 px-3 font-mono text-emerald-400 font-bold">{lic.license_key}</td>
                                                                    <td className="py-3 px-3 text-slate-400 text-xs">
                                                                        {new Date(lic.created_at).toLocaleString('fa-IR')}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* تب دوم: گزارش عملکرد + مدیریت دسترسی آنلاین و زنده همکاران */
                                <div className="glass-panel p-6 rounded-2xl border border-cyan-500/20 fade-in space-y-8">
                                    <div className="flex justify-between items-center border-b border-slate-700 pb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#0ef]"></span>
                                                مدیریت دسترسی محصولات و سهمیه همکاران
                                            </h3>
                                            <p className="text-xs text-slate-400 mt-1">تغییر آنی محصولات مجاز و اعتبار نمایندگان بدون نیاز به تغییر مستقیم در دیتابیس</p>
                                        </div>
                                        <button 
                                            onClick={fetchResellerStats}
                                            className="bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs border border-slate-700 transition-colors"
                                        >
                                            {statsLoading ? 'در حال بروزرسانی...' : 'بروزرسانی کل داده‌ها'}
                                        </button>
                                    </div>

                                    {/* بخش تعاملی ویرایش مستقیم و دکمه‌های تیک‌زدنی دسترسی */}
                                    <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-800/60 overflow-x-auto">
                                        <table className="w-full text-sm text-right licenses-table">
                                            <thead className="text-slate-400 border-b border-slate-700 text-xs">
                                                <tr>
                                                    <th className="py-2 px-3">نام همکار (نماینده)</th>
                                                    <th className="py-2 px-3 text-center">محصولات مجاز برای صدور (تیک فعال‌سازی نئون)</th>
                                                    <th className="py-2 px-3 text-center">کل اعتبار (سهمیه لایسنس)</th>
                                                    <th className="py-2 px-3 text-center">مصرف شده</th>
                                                    <th className="py-2 px-3 text-center">کل لایسنس‌های موفق</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-white text-xs">
                                                {resellerStats.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center text-slate-500 py-6">داده‌ای جهت مدیریت یافت نشد.</td>
                                                    </tr>
                                                ) : (
                                                    resellerStats.map((reseller, index) => {
                                                        const allowedString = reseller.allowed_products || '';
                                                        const activeProducts = allowedString.split(',').map(p => p.trim());
                                                        const isResellerUpdating = updatingId === reseller.id;

                                                        return (
                                                            <tr key={reseller.id || index} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors">
                                                                <td className="py-3 px-3">
                                                                    <span className="font-bold block text-white text-sm">{reseller.name}</span>
                                                                    <span className="text-[10px] text-slate-500 font-mono">شناسه کاربری: {reseller.id}</span>
                                                                </td>
                                                                <td className="py-3 px-3 text-center">
                                                                    <div className="flex gap-2 justify-center flex-wrap">
                                                                        {[
                                                                            { id: 'SteeliCa', label: 'SteeliCa' },
                                                                            { id: 'BetoniCa', label: 'BetoniCa' },
                                                                            { id: 'EtaNex', label: 'EtaNex' },
                                                                            { id: 'SeismiCa', label: 'SeismiCa' },
																			{ id: 'WalliCa', label: 'WalliCa' },
                                                                        { id: 'StructiCa', label: 'StructiCa' }
                                                                        ].map(prod => {
                                                                            const isToggled = activeProducts.includes(prod.id);
                                                                            return (
                                                                                <button
                                                                                    key={prod.id}
                                                                                    type="button"
                                                                                    disabled={isResellerUpdating}
                                                                                    onClick={() => handleToggleProduct(reseller, prod.id)}
                                                                                    className={`neon-toggle-btn ${isToggled ? 'active' : ''}`}
                                                                                    title={`تغییر دسترسی به ${prod.label}`}
                                                                                >
                                                                                    <span>{isToggled ? '✓' : '✗'}</span>
                                                                                    <span>{prod.label}</span>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-3 text-center">
                                                                    <input 
                                                                        type="number"
                                                                        defaultValue={reseller.allowed_credits ?? 0}
                                                                        disabled={isResellerUpdating}
                                                                        onBlur={(e) => {
                                                                            const val = parseInt(e.target.value) || 0;
                                                                            if (val !== reseller.allowed_credits) {
                                                                                handleUpdateResellerAccess(reseller.id, allowedString, val);
                                                                            }
                                                                        }}
                                                                        className="w-16 bg-slate-950 border border-slate-800 text-center rounded-lg py-1 px-1 text-cyan-400 font-mono text-xs font-bold focus:outline-none focus:border-cyan-500 transition-colors"
                                                                    />
                                                                </td>
                                                                <td className="py-3 px-3 text-center font-mono text-slate-400 text-sm">
                                                                    {reseller.used_credits ?? 0}
                                                                </td>
                                                                <td className="py-3 px-3 text-center">
                                                                    <span className="text-emerald-400 font-bold font-mono bg-emerald-500/10 px-2.5 py-1 rounded">
                                                                        {reseller.sales ?? 0}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* بخش نمودارهای گرافیکی خطی مقایسه‌ای قبلی */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                        <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/40">
                                            <h4 className="text-sm font-bold text-slate-300 mb-4">جدول رتبه‌بندی عملکرد فروش نمایندگان</h4>
                                            <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
                                                {resellerStats.map((reseller, index) => (
                                                    <div key={index} className="flex justify-between items-center p-3 bg-slate-900/80 rounded-lg border border-slate-800 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-mono text-slate-500 bg-slate-950 px-1.5 py-0.5 rounded">
                                                                {index + 1}
                                                            </span>
                                                            <span className="text-white font-medium">{reseller.name}</span>
                                                        </div>
                                                        <div className="text-emerald-400 font-bold font-mono bg-emerald-500/10 px-2.5 py-1 rounded">
                                                            {reseller.sales} <span className="text-[10px] text-slate-400 font-normal mr-0.5">فروش</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/40 flex flex-col justify-between">
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-300 mb-6">نمودار مقایسه خطی لایسنس‌های فروخته شده</h4>
                                                <div className="space-y-4">
                                                    {resellerStats.map((reseller, index) => {
                                                        const barWidth = maxSales > 0 ? (reseller.sales / maxSales) * 100 : 0;
                                                        return (
                                                            <div key={index} className="space-y-1">
                                                                <div className="flex justify-between text-xs text-slate-400">
                                                                    <span>{reseller.name}</span>
                                                                    <span className="font-mono text-cyan-400 font-bold">{reseller.sales} مورد</span>
                                                                </div>
                                                                <div className="w-full bg-slate-950 rounded-full h-2.5 overflow-hidden border border-slate-800">
                                                                    <div 
                                                                        className="bg-gradient-to-r from-cyan-500 to-emerald-400 h-full rounded-full transition-all duration-700"
                                                                        style={{ width: `${barWidth}%`, minWidth: reseller.sales > 0 ? '4%' : '0%' }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* دکمه خروج انتهای پنل */}
                            <div className="lg:col-span-2 text-center mt-8">
                                <button 
                                    onClick={handleLogout} 
                                    className="text-red-400 hover:text-red-300 text-sm border border-red-900/30 bg-red-900/10 px-6 py-2 rounded-full transition-colors"
                                >
                                    خروج از حساب مدیریت
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginAdmin;